<template>
  <!-- <pre>{{ team }}</pre> -->
  <BlocksHeroSection class="bg-gray-900 dark:bg-neutral-800 text-white">
    <div class="flex flex-col lg:flex-row justify-between w-full">
      <div class="flex flex-col grow">
        <div v-if="team">
          <div class="flex items-center gap-2">
            <TeamLogo :team="team" size="xl" />
            <HeadingBasic :size="1">{{ team.name }}</HeadingBasic>
          </div>
        </div>
        <small class="block text-base md:text-lg lg:text-2xl mt-3">
          <a href="#" @click.prevent="$router.back()" class="font-light">
            <i class="fal fa-chevron-left"></i>
            Back to teams
          </a>
        </small>
      </div>
      <div
        class="flex lg:items-center flex-wrap gap-y-10 mt-10 lg:mt-0 lg:flex-nowrap gap-4 sm:gap-10"
        v-if="team"
      >
        <DataPoint class="lg:w-auto" v-if="team.conference"
          >Conference<template #value
            >{{ team.conference.abbreviation }} ({{
              team.conference.division
            }})</template
          ></DataPoint
        >
        <DataPoint class="lg:w-auto">
          2025 Record<template #value>{{
            team.schedule ? sumBaseballRecords(team.schedule[0]) : 'N/A'
          }}</template>
        </DataPoint>
        <DataPoint class="lg:w-auto">
          2025 RPI<template #value>{{ team.current_rpi || '--' }}</template>
        </DataPoint>
        <DataPoint class="lg:w-auto">
          64 Ranking<template #value>{{
            team.team_rank && team.team_rank.length
              ? team.team_rank[team.team_rank.length - 1]
                  .integer_64_rank_total || '--'
              : '--'
          }}</template>
        </DataPoint>
      </div>
    </div>
  </BlocksHeroSection>
  <section class="bg-neutral-50 dark:bg-neutral-900 py-4 flex-1" v-if="team">
    <ContainerBasic class="!px-0">
      <div ref="placeholderRef" class="use-sticky-placeholder-ref"></div>
      <div
        ref="tabRef"
        class="w-calc(100%_+_1.25rem) shadow transition-transform bg-white dark:bg-neutral-900 z-[4]"
        :class="{
          // '-translate-y-[var(--header-height)]': isSticky && headerVisible,
          'translate-y-[var(--header-height)]': isSticky && headerVisible,
        }"
      >
        <ContainerBasic>
          <div>
            <TabsBasic scroll-to-top :tabs="tabs" v-model="currentTabKey">
              <template #advanced-stats-tab="{ tab }">
                <i
                  v-if="!hasAccess"
                  class="fas fa-lock text-cardinal dark:text-white mr-2"
                />
                {{ tab.label }}
              </template>
              <template #rankings-tab="{ tab }">
                <i
                  v-if="!hasAccess"
                  class="fas fa-lock text-cardinal dark:text-white mr-2"
                />
                {{ tab.label }}
              </template>
            </TabsBasic>
          </div>
        </ContainerBasic>
      </div>

      <UCard class="pt-5" v-if="currentTab && currentTab.key == 'depth-chart'">
        <div class="">
          <div class="flex justify-between mb-3 md:mb-8">
            <div>
              <HeadingBasic :size="2">Depth Chart</HeadingBasic>
            </div>
          </div>
        </div>
        <TableSortable
          :loading="depthChartLoading"
          v-model:sort="depthChartSort"
          :total="depthChart.length"
          :per-page="1000"
          v-model:year="depthChartYear"
          @update:sort="refreshDepthChart"
          v-if="depthChart && depthChart.slice"
          :ui="{
            tr: {
              base: `even:bg-neutral-100 dark:bg-neutral-900 dark:even:bg-neutral-950`,
            },
          }"
          :columns="[
            {
              key: 'year',
              label: 'Year',
              sortable: true,
              class: '!text-left',
            },
            {
              key: 'player_name',
              label: 'Name',
              sortable: true,
              class: '!text-left',
            },
            {
              key: 'position',
              label: 'Position',
              sortable: true,
            },
            {
              key: 'classification',
              label: 'Yr',
              sortable: true,
            },
            {
              key: 'games_played',
              label: 'Games Played',
              sortable: true,
              direction: 'desc',
            },
            {
              key: 'games_started',
              label: 'Games Started',
              sortable: true,
              direction: 'desc',
            },
            {
              key: 'games_appeared',
              label: 'Games Appeared',
              sortable: true,
              direction: 'desc',
            },
            {
              key: 'stats',
              label: 'Stats',
            },
          ]"
          :rows="depthChart ?? []"
        >
          <template #player_name-data="{ row }">
            <TableCellLink left :path="`/players/${row.player_id}`">{{
              row.player_name
            }}</TableCellLink>
          </template>
          <template #playing_time-data="{ row }">
            {{ (row.playing_time * 100).toFixed(2) }}%
          </template>
          <template #stats-data="{ row }">
            <UButton
              color="white"
              @click="() => navigateTo(`/players/${row.player_id}`)"
              round
              >View Stats</UButton
            >
          </template>
        </TableSortable>
      </UCard>
      <UCard class="pt-5" v-if="currentTab && currentTab.key == 'recruiting'">
        <!-- <h2 class="font-extrabold text-3xl mt-8">Recruiting</h2> -->

        <!-- <DataPointsGrid :data="team.teams_hs[0]" /> -->

        <UCard
          class="mt-3 md:mt-8 !bg-cardinal-900 dark:!bg-cardinal-800 overflow-hidden text-white p-0 md:p-10 before:content-[''] before:absolute before:block before:z-[2] before:top-0 before:right-0 before:w-full before:h-full before:bg-[url('/images/64-Analytics_Emblem.png')] before:bg-no-repeat before:bg-right before:bg-black relative before:opacity-10"
        >
          <div
            v-if="team.teams_hs && team.teams_hs[0]"
            class="lg:flex gap-10 lg:gap-20"
          >
            <div class="flex flex-col 2xl:grow 2xl:basis-0">
              <HeadingBasic :size="2" class="mb-8"
                >Recruiting - Last 5 Years</HeadingBasic
              >
              <div class="flex flex-col gap gap-5">
                <div class="flex gap-5">
                  <DataPoint>
                    Top 500 Players <br />Committed
                    <template #value>
                      {{ team.teams_hs[0].five_yr_hs_commit }}
                    </template>
                  </DataPoint>
                  <DataPoint>
                    Top 500 Players Drafted <br />Out of High School
                    <template #value>
                      {{ team.teams_hs[0].five_yr_hs_signed }}
                    </template>
                  </DataPoint>
                  <!-- <DataPoint>
                  Drafted
                  <template #value>
                    {{ team.teams_hs[0].five_yr_draft }}
                  </template>
                </DataPoint> -->
                </div>
              </div>
            </div>
            <div
              class="flex mb-5 md:mb-0 flex-col grow 2xl:basis-0 gap gap-5 mt-10 lg:mt-0 lg:border-l border-l-cardinal-400 justify-center lg:pl-8"
            >
              <DataBar
                label="Total Signing Bonus Last 5 Years"
                :max="100"
                stack-on-mobile
                v-model="signingBonusPct"
                :restrict-left-col="false"
                :left-label-class="'!text-white'"
                :value-label-class="'!text-white'"
                :right-label-class="'!text-white'"
                :raw-value="`$${formatNumber(
                  team.teams_hs[0].bonus_value || 0
                )}`"
              />
              <!-- <div class="flex gap-5">
              <DataPoint>
                Draft
                <template #value
                  >{{ parseInt(team.teams_hs[0].stand_hs_draft * 100) }}th</template
                >
              </DataPoint>
              <DataPoint>
                School
                <template #value
                  >{{ parseInt(team.teams_hs[0].stand_hs_school * 100) }}th</template
                >
              </DataPoint>
            </div> -->
            </div>
          </div>
          <div class="relative z-[2]" v-else>
            <HeadingBasic :size="4"
              >Coming Soon for D-I and D-II teams</HeadingBasic
            >
            <p>
              <a class="underline" href="https://x.com/@64analytics"
                >Follow us on X @64analytics</a
              >
              to stay up to date
            </p>
          </div>
        </UCard>

        <UCard class="mt-3">
          <TableSortable
            :columns="[
              { key: 'year', label: 'Year', sortable: true },
              {
                key: 'committed_team',
                label: 'Team',
                sortable: true,
                class: '!text-left',
              },
              {
                key: 'player_name',
                label: 'Name',
                sortable: true,
                class: '!text-left',
              },
              { key: 'drafted', label: 'Drafted - HS', sortable: true },
              { key: 'hs_position', label: 'Position', sortable: true },
              { key: 'stats', label: 'Stats', sortable: false },
            ]"
            :rows="playersHs"
            plural="Players"
            v-model:year="playersHsYear"
            v-model:page="playersHsPage"
            v-model:per-page="playersHsPerPage"
            v-model:total="playersHsTotal"
            v-model:sort="playersHsSort"
            v-model:page-count="playersHsPageCount"
            :loading="playersHsLoading"
            @refresh="refreshPlayersHs"
          >
            <template #title>
              <HeadingBasic :size="3">High School Players</HeadingBasic>
            </template>
            <template #committed_team-data="{ row }">
              <TableCellBase left>{{ row.committed_team.name }}</TableCellBase>
            </template>
            <template #player_name-data="{ row }">
              <template v-if="!row.player_id">
                <TableCellBase left>{{ row.player_name }}</TableCellBase>
              </template>
              <TableCellLink v-else left :path="`/players/${row.player_id}`">
                {{ row.player_name }}
              </TableCellLink>
            </template>
            <template #stats-data="{ row }">
              <template v-if="row.player_id">
                <UButton
                  :to="{ path: `/players/${row.player_id}` }"
                  color="white"
                  >View Stats</UButton
                >
              </template>
              <span v-else>&nbsp;</span>
            </template>
            <template #drafted-data="{ row }">
              <span v-if="row.drafted == 'TRUE'">
                <i
                  class="fas fa-check mx-auto text-green-600 dark:text-green-400"
                ></i>
              </span>
              <span v-else> &nbsp; </span>
            </template>
          </TableSortable>
        </UCard>
        <UCard class="mt-3">
          <TableSortable
            :columns="portalRankPlayersColumns"
            :rows="portalRankPlayers"
            :dictionary="prpDictionary"
            plural="Players"
            v-model:year="portalRankPlayersYear"
            v-model:page="portalRankPlayersPage"
            v-model:per-page="portalRankPlayersPerPage"
            v-model:total="portalRankPlayersTotal"
            v-model:sort="portalRankPlayersSort"
            v-model:page-count="portalRankPlayersPageCount"
            :loading="portalRankPlayersLoading"
            @refresh="refreshPortalRankPlayers"
          >
            <template #title>
              <HeadingBasic :size="3">Portal Players</HeadingBasic>
            </template>
            <template #player_name-data="{ row }">
              <template v-if="!row.player_id"> {{ row.player_name }}</template>
              <TableCellLink v-else left :path="`/players/${row.player_id}`">
                {{ row.player_name }}
              </TableCellLink>
            </template>
            <template #previous_team_name-data="{ row }">
              <TableCellLink left :path="`/teams/${row.previous_team_id}`">{{
                row.previous_team_name
              }}</TableCellLink>
            </template>
            <template #new_team_name-data="{ row }">
              <TableCellLink left :path="`/teams/${row.new_team_id}`">{{
                row.new_team_name
              }}</TableCellLink>
            </template>
            <template #stats-data="{ row }">
              <UButton
                v-if="row.player_id"
                color="white"
                :to="{ path: `/players/${row.player_id}` }"
                round
                >View Stats</UButton
              >
              <span v-else>&nbsp;</span>
            </template>
          </TableSortable>
        </UCard>

        <!-- <h3 class="font-extrabold text-2xl mt-8 mb-4">Breakdown</h3>
        <table class="min-w-[200px]">
          <tr class="border-t border-t-gray-400">
            <th class="text-left">Pitchers</th>
            <td class="text-left">
              <template v-if="draftHistory">
                {{
                  draftHistory.filter(
                    (d) => d.draft_position.includes('p') || d.draft_position.includes('P')
                  ).length
                }}
              </template>
            </td>
          </tr>
          <tr class="border-t border-t-gray-400">
            <th class="text-left">Positions</th>
            <td class="text-left">
              <template v-if="draftHistory">
                {{
                  draftHistory.filter(
                    (d) => !d.draft_position.includes('p') && !d.draft_position.includes('P')
                  ).length
                }}
              </template>
            </td>
          </tr>
          <tr class="border-t border-t-gray-400">
            <th class="text-left">Total</th>
            <td class="text-left">
              <template v-if="draftHistory">
                {{ draftHistory.length }}
              </template>
            </td>
          </tr>
        </table> -->
      </UCard>
      <UCard
        class="pt-5"
        v-if="currentTab && currentTab.key == 'coaching-staff'"
      >
        <div class="mb-3 md:mb-8 flex justify-between">
          <HeadingBasic :size="2">Coaching Staff</HeadingBasic>
        </div>
        <StaffList :staff="team.staff" />
      </UCard>
      <UCard
        class="pt-5"
        v-if="currentTab && currentTab.key == 'draft-history'"
      >
        <div class="mb-3 md:mb-8 flex justify-between">
          <div>
            <HeadingBasic :size="2">Draft History</HeadingBasic>
          </div>
        </div>
        <TableSortable
          v-if="draftHistory && draftHistory.slice"
          :loading="draftHistoryLoading"
          v-model:sort="draftHistorySort"
          @update:sort="refreshDraftHistory"
          :total="draftHistory.length"
          :per-page="50"
          v-model:year="draftHistoryYear"
          :max-year="null"
          :min-year="2019"
          :rows="draftHistory"
          :columns="[
            { key: 'year', label: 'Year', sortable: true },
            { key: 'round', label: 'Round', sortable: true },
            { key: 'pick', label: 'Pick', sortable: true },
            {
              key: 'player_name',
              label: 'Name',
              sortable: true,
              class: '!text-left',
            },
            { key: 'draft_position', label: 'Position', sortable: true },
            {
              key: 'team_name',
              label: 'School',
              sortable: true,
              class: '!text-left',
            },
            { key: 'classification_drafted', label: 'Type', sortable: true },
            {
              key: 'signing_bonus',
              label: 'Signing Bonus',
              sortable: true,
              direction: 'desc',
            },
            {
              key: 'pick_differential',
              label: 'Pick Differential',
              sortable: true,
              direction: 'desc',
            },
          ]"
        >
          <template #player_name-data="{ row }">
            <TableCellBase left>{{ row.player_name }}</TableCellBase>
          </template>
          <template #team_name-data="{ row }">
            <TableCellBase left>{{ team.name }}</TableCellBase>
          </template>
          <template #signing_bonus-data="{ row }">
            <span class=""> ${{ formatNumber(row.signing_bonus) }} </span>
          </template>
          <template #pick_differential-data="{ row }">
            <span class=""
              >{{ (row.pick_differential * 100).toFixed(2) }}%</span
            >
          </template>
        </TableSortable>
      </UCard>
      <UCard class="pt-5" v-if="currentTab && currentTab.key == 'rankings'">
        <HeadingBasic :size="2">Rankings</HeadingBasic>
        <DataRankingsWidget
          :hittingBarData="hittingBarData"
          :barYearHitting="barYearHitting"
          :hitting-rankings="teamRankHitting"
          :pitchingBarData="pitchingBarData"
          :barYearPitching="barYearPitching"
          :pitching-rankings="teamRankPitching"
          v-model:bar-year="barYear"
          :type="'team'"
          :restricted="!hasAccess"
        />
        <div class="flex flex-col gap-10 mt-5">
          <TableSortable
            :default-columns="defaultHittingRankCols"
            :selected-columns="selectedHittingRankColumns"
            :columns="teamRankHittingColumns"
            :rows="teamRankHitting"
            :dictionary="teamRankHittingDictionary"
            :restricted="!hasAccess"
            v-model:sort="teamRankHittingSort"
            :loading="teamRankHittingLoading"
            @refresh="refreshTeamRankingsHitting"
            @selected-columns-update="onSelectedHittingRankColumnsChange"
          >
            <template #title>
              <HeadingBasic :size="4">Hitting</HeadingBasic>
            </template>
          </TableSortable>
          <TableSortable
            :default-columns="defaultPitchingRankCols"
            :selected-columns="selectedPitchingRankColumns"
            :columns="teamRankPitchingColumns"
            :rows="teamRankPitching"
            :dictionary="teamRankPitchingDictionary"
            :restricted="!hasAccess"
            v-model:sort="teamRankPitchingSort"
            :loading="teamRankPitchingLoading"
            @refresh="refreshTeamRankingsPitching"
            @selected-columns-update="onSelectedPitchingRankColumnsChange"
          >
            <template #title>
              <HeadingBasic :size="4">Pitching</HeadingBasic>
            </template>
            <template #year-data="{ row }">
              <TableCellBase left>{{ row.year }}</TableCellBase>
            </template>
          </TableSortable>
        </div>
      </UCard>
      <UCard class="pt-5" v-if="currentTab && currentTab.key == 'stats'">
        <HeadingBasic :size="2">Stats</HeadingBasic>
        <div class="flex flex-col gap-10 mt-5">
          <div>
            <TableSortable
              :rows="hittingStats"
              :default-columns="defaultBasicHittingCols"
              :selected-columns="selectedBasicHittingCols"
              :columns="basicHittingCols"
              :dictionary="hittingDictionary"
              reverse-filter-flex-order
              @selected-columns-update="onSelectedBasicHittingColumnsChange"
            >
              <template #title>
                <HeadingBasic :size="4">Hitting</HeadingBasic>
              </template>
              <template #year-data="{ row }">
                <TableCellBase left>{{ row.year }}</TableCellBase>
              </template>
            </TableSortable>
            <div v-if="!hasAccess" class="flex justify-end mt-3">
              <UButton color="black" @click="() => navigateTo('/sign-up')">
                Sign up for Advanced Statistics
              </UButton>
            </div>
          </div>
          <div>
            <TableSortable
              :rows="fieldingStats"
              :default-columns="defaultBasicFieldingCols"
              :selected-columns="selectedBasicFieldingCols"
              :columns="basicFieldingCols"
              :dictionary="fieldingDictionary"
              reverse-filter-flex-order
              @selected-columns-update="onSelectedBasicFieldingColumnsChange"
            >
              <template #title>
                <HeadingBasic :size="4">Fielding</HeadingBasic>
              </template>
              <template #year-data="{ row }">
                <TableCellBase left>{{ row.year }}</TableCellBase>
              </template>
            </TableSortable>
            <div v-if="!hasAccess" class="flex justify-end mt-3">
              <UButton color="black" @click="() => navigateTo('/sign-up')">
                Sign up for Advanced Statistics
              </UButton>
            </div>
          </div>
          <div>
            <TableSortable
              :rows="pitchingStats"
              :default-columns="defaultBasicPitchingCols"
              :selected-columns="selectedBasicPitchingCols"
              :columns="basicPitchingCols"
              :dictionary="pitchingDictionary"
              reverse-filter-flex-order
              @selected-columns-update="onSelectedBasicPitchingColumnsChange"
            >
              <template #title>
                <HeadingBasic :size="4">Pitching</HeadingBasic>
              </template>
              <template #year-data="{ row }">
                <TableCellBase left>{{ row.year }}</TableCellBase>
              </template>
            </TableSortable>
            <div v-if="!hasAccess" class="flex justify-end mt-3">
              <UButton color="black" @click="() => navigateTo('/sign-up')">
                Sign up for Advanced Statistics
              </UButton>
            </div>
          </div>
        </div>
      </UCard>
      <UCard
        class="pt-5"
        v-if="currentTab && currentTab.key == 'advanced-stats'"
      >
        <HeadingBasic :size="2">Advanced Stats</HeadingBasic>
        <div class="flex flex-col gap-10 mt-5">
          <TableSortable
            :rows="advancedHittingStats"
            :default-columns="defaultAdvancedHittingCols"
            :selected-columns="selectedAdvancedHittingCols"
            :columns="advancedHittingCols"
            :dictionary="hittingDictionary"
            :restricted="!hasAccess"
            reverse-filter-flex-order
            @selected-columns-update="onSelectedAdvancedHittingColumnsChange"
          >
            <template #title>
              <HeadingBasic :size="4">Hitting</HeadingBasic>
            </template>
          </TableSortable>
          <TableSortable
            :rows="advancedPitchingStats"
            :default-columns="defaultAdvancedPitchingCols"
            :selected-columns="selectedAdvancedPitchingCols"
            :columns="advancedPitchingCols"
            :dictionary="pitchingDictionary"
            :restricted="!hasAccess"
            reverse-filter-flex-order
            @selected-columns-update="onSelectedAdvancedPitchingColumnsChange"
          >
            <template #title>
              <HeadingBasic :size="4">Pitching</HeadingBasic>
            </template>
          </TableSortable>
        </div>
      </UCard>
      <UCard class="pt-5" v-if="currentTab && currentTab.key == 'development'">
        <HeadingBasic :size="2">Development</HeadingBasic>
        <div class="mt-10 max-w-[960px]">
          <UCard
            class="relative after:content-[''] after:absolute after:block after:rounded-xl after:z-[1] after:bg-cardinal after:-top-6 after:-left-6 after:w-full after:h-full mt-6 ml-6 p-0 ad-card-wrapper"
          >
            <div
              class="overflow-hidden bg-black relative flex flex-col lg:flex-row gap-10 lg:justify-between lg:items-center rounded-xl p-10 lg:px-10 lg:py-20 z-[2] before:content-[''] before:absolute before:block before:z-[2] before:top-0 before:left-0 before:w-full before:h-full before:bg-[url('/images/64-Analytics_Emblem.png')] before:bg-no-repeat before:bg-center before:opacity-30"
            >
              <div
                class="flex flex-col lg:flex-row lg:items-center gap-5 relative z-[3]"
              >
                <img
                  class="z-[1] object-fill max-w-full"
                  width="55px"
                  height="55px"
                  src="~/assets/images/64_Analytics_Emblem_1Color.svg"
                  alt=""
                />
                <div class="flex flex-col">
                  <HeadingBasic class="text-white" :size="3"
                    >Development rankings and statistics are coming
                    soon</HeadingBasic
                  >
                  <p class="text-cardinal-50">
                    Stay up to date on the latest news from 64 Analytics over on
                    X
                  </p>
                </div>
              </div>
              <a
                href="https://twitter.com/64Analytics"
                target="_blank"
                class="bg-white rounded-full px-4 py-2 self-start font-extrabold flex gap-2 items-center text-cardinal-900 relative z-[2] lg:px-5 lg:py-3 tracking-tight hover:bg-cardinal hover:text-white"
              >
                Follow us on <i class="fab fa-x-twitter inline-block"></i>
              </a>
            </div>
          </UCard>
        </div>
      </UCard>

      <!-- <UTable v-if="player" :rows="[player]"></UTable> -->
    </ContainerBasic>
  </section>
</template>

<script setup lang="ts">
// import * as Sentry from '@sentry/vue';
import type { Views } from '~/types/database-helpers';
import { truncateToThreeDecimals } from '~/lib/truncate-to-three-decimals';
import { RadioGroup, RadioGroupLabel, RadioGroupOption } from '@headlessui/vue';
import {
  basicTeam as basicHittingColumns,
  advancedTeam as advancedHittingColumns,
} from '~/lib/table-columns/hitting';
import {
  basicTeam as basicPitchingColumns,
  advancedTeam as advancedPitchingColumns,
} from '~/lib/table-columns/pitching';
import { basicTeam as basicFieldingColumns } from '~/lib/table-columns/fielding';
import { basic as teamRankHittingCols } from '~/lib/table-columns/team-rank-hitting';
import { basic as teamRankPitchingCols } from '~/lib/table-columns/team-rank-pitching';
import type { Database } from '~/types';
import { getQueryPagination } from '~/types/database-query.d';
import type { Column } from '~/types/data-dictionary';
const supabase = useSupabaseClient<Database>();
// const teamsService = new TeamsService(supabase);
// const hittingService = new HittingService(supabase);
// const fieldingService = new FieldingService(supabase);
// const pitchingService = new PitchingService(supabase);
const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-US').format(num);
};
const route = useRoute();
const teamId = route.params.id.toString();
const store = useProfileStore();
const { hasAccess } = storeToRefs(store);
const temp = ref(35);
const fakeVal = ref(93.3);
const barColor = computed(() => {
  switch (true) {
    case temp.value < 10:
      return 'blue';
    case temp.value < 20:
      return 'amber';
    case temp.value < 30:
      return 'orange';
    default:
      return 'red';
  }
});
const depthChartYear = ref(2025);
const depthChartSort = ref<{ column: string; direction: 'asc' | 'desc' }>({
  column: 'player_name',
  direction: 'asc',
});
const draftHistoryYear = ref('');
const draftHistorySort = ref<{ column: string; direction: 'asc' | 'desc' }>({
  column: 'id',
  direction: 'asc',
});

/**
 * hitting
 */
const HITTING_TABLE_NAMES = ['hitting', 'fielding'];
const { parseData: hittingParseData, localDictionary: hittingDictionary } =
  useDataDictionary(HITTING_TABLE_NAMES);
// Cols - Basic
const defaultBasicHittingCols = transformColumns(
  basicHittingColumns,
  hittingDictionary.value
);
const selectedBasicHittingCols = ref([...defaultBasicHittingCols]);
const basicHittingCols = computed((): Column[] => {
  return defaultBasicHittingCols.filter((column) => {
    return selectedBasicHittingCols.value.find((col) => col.key === column.key);
  });
});
const onSelectedBasicHittingColumnsChange = ($event: Column[]) => {
  selectedBasicHittingCols.value = $event;
};
// Cols - Advanced
const defaultAdvancedHittingCols = transformColumns(
  advancedHittingColumns,
  hittingDictionary.value
);
const selectedAdvancedHittingCols = ref([...defaultAdvancedHittingCols]);
const advancedHittingCols = computed((): Column[] => {
  return defaultAdvancedHittingCols.filter((column) => {
    return selectedAdvancedHittingCols.value.find(
      (col) => col.key === column.key
    );
  });
});
const onSelectedAdvancedHittingColumnsChange = ($event: Column[]) => {
  selectedAdvancedHittingCols.value = $event;
};
/**
 * Pitching
 */
const PITCHING_TABLE_NAMES = ['pitching'];
const { parseData: pitchingParseData, localDictionary: pitchingDictionary } =
  useDataDictionary(PITCHING_TABLE_NAMES);
// Cols - Basic
const defaultBasicPitchingCols = transformColumns(
  basicPitchingColumns,
  pitchingDictionary.value
);
const selectedBasicPitchingCols = ref([...defaultBasicPitchingCols]);
const basicPitchingCols = computed((): Column[] => {
  return defaultBasicPitchingCols.filter((column) => {
    return selectedBasicPitchingCols.value.find(
      (col) => col.key === column.key
    );
  });
});
const onSelectedBasicPitchingColumnsChange = ($event: Column[]) => {
  selectedBasicPitchingCols.value = $event;
};
// Cols - Advanced
const defaultAdvancedPitchingCols = transformColumns(
  advancedPitchingColumns,
  pitchingDictionary.value
);
const selectedAdvancedPitchingCols = ref([...defaultAdvancedPitchingCols]);
const advancedPitchingCols = computed((): Column[] => {
  return defaultAdvancedPitchingCols.filter((column) => {
    return selectedAdvancedPitchingCols.value.find(
      (col) => col.key === column.key
    );
  });
});
const onSelectedAdvancedPitchingColumnsChange = ($event: Column[]) => {
  selectedAdvancedPitchingCols.value = $event;
};

/**
 * Fielding
 */
const FIELDING_TABLE_NAMES = ['fielding'];
const { parseData: fieldingParseData, localDictionary: fieldingDictionary } =
  useDataDictionary(FIELDING_TABLE_NAMES);
// Cols - Basic
const defaultBasicFieldingCols = transformColumns(
  basicFieldingColumns,
  fieldingDictionary.value
);
const selectedBasicFieldingCols = ref([...defaultBasicFieldingCols]);
const basicFieldingCols = computed((): Column[] => {
  return defaultBasicFieldingCols.filter((column) => {
    return selectedBasicFieldingCols.value.find(
      (col) => col.key === column.key
    );
  });
});
const onSelectedBasicFieldingColumnsChange = ($event: Column[]) => {
  selectedBasicFieldingCols.value = $event;
};
const { data: team } = await useAsyncData(`team-${teamId}`, async () => {
  try {
    let { data: team, error } = await supabase
      .from('teams')
      .select(
        `
      *,
      staff:staff(*),
      conference:conferences(*),
      teams_hs:teams_hs(*),
      schedule:schedules(*),
      hitting:hitting_team(*),
      pitching:pitching_team(*),
      fielding:fielding_team(*),
      team_rank:team_rank(integer_64_rank_total)
    `
      )
      .order('name', { ascending: true })
      .order('name', { ascending: true })
      .eq('id', teamId)
      .eq('team_rank.year', 2025)
      .eq('schedule.year', 2025)
      .single();

    if (error) {
      console.error('Error finding team', error);
      throw error;
    }

    return team;
  } catch (error) {
    // Sentry.captureException(error);
    return null;
  }
});
const signingBonusPct = ref(
  (team.value?.teams_hs &&
  team.value.teams_hs[0] &&
  team.value.teams_hs[0].signing_bonus_percent
    ? team.value.teams_hs[0].signing_bonus_percent * 100
    : 0
  ).toFixed(2)
);
useHead({
  title: `${team?.value?.name}` + ' | In-Depth NCAA Baseball Team Analysis',
  meta: [
    {
      name: 'description',
      content:
        'Get a closer look at NCAA baseball teams with our in-depth analytics. Explore team strategies, player performances, and historical data.',
    },
  ],
});

const {
  data: draftHistory,
  refresh: refreshDraftHistory,
  pending: draftHistoryLoading,
} = await useAsyncData(
  `team-${teamId}-draftHistory`,
  async () => {
    const query = supabase
      .from('drafts')
      .select(`*`)
      .order(draftHistorySort.value.column, {
        ascending: draftHistorySort.value.direction == 'asc',
      });
    if (draftHistoryYear.value) {
      query.eq('year', draftHistoryYear.value);
    }
    query.eq('team_id', teamId).limit(1000);

    let { data: draftHistory, error } = await query;

    if (error) {
      return error;
    }

    return draftHistory;
  },
  {
    watch: [draftHistoryYear],
  }
);
const playersHsSort = ref<{ column: string; direction: 'asc' | 'desc' }>({
  column: 'year',
  direction: 'desc',
});
const playersHsPage = ref(1);
const playersHsPerPage = ref(50);
const playersHsYear = ref(2025);
const playersHsTotal = ref(0);
const playersHsPageCount = ref(0);
const {
  data: playersHs,
  refresh: refreshPlayersHs,
  pending: playersHsLoading,
} = await useAsyncData(
  `team-${teamId}-playersHs`,
  async () => {
    try {
      const pagination = getQueryPagination(
        playersHsPage.value,
        playersHsPerPage.value
      );
      const query = supabase
        .from('players_hs')
        .select(`*, committed_team:committed_team(id, name)`, {
          count: 'exact',
        })
        .range(pagination.startIndex, pagination.endIndex)
        .limit(playersHsPerPage.value)
        .order(playersHsSort.value.column, {
          ascending: playersHsSort.value.direction == 'asc',
        });
      if (playersHsYear.value) {
        query.eq('year', playersHsYear.value);
      }
      query.eq('committed_team', teamId).limit(1000);

      let { data, error, count } = await query;

      if (error) {
        console.error(error);
        throw error;
      }

      if (count) {
        playersHsTotal.value = count;
        playersHsPageCount.value = Math.ceil(count / playersHsPerPage.value);
      } else {
        playersHsTotal.value = 0;
        playersHsPageCount.value = 0;
      }

      return data;
    } catch (error) {
      // Sentry.captureException(error);
      return [];
    }
  },
  {
    watch: [playersHsYear],
  }
);
const portalRankPlayersSort = ref<{
  column: string;
  direction: 'asc' | 'desc';
}>({
  column: 'sixty_four_rating_portal_player',
  direction: 'desc',
});
const portalRankPlayersPage = ref(1);
const portalRankPlayersPerPage = ref(50);
const portalRankPlayersYear = ref(2025);
const portalRankPlayersTotal = ref(0);
const portalRankPlayersPageCount = ref(0);
const PRP_TABLE_NAMES = ['portal_rank_player'];
const { parseData: prpParseData, localDictionary: prpDictionary } =
  useDataDictionary(PRP_TABLE_NAMES);
const portalRankPlayersColumns = computed(() => {
  let cols = [
    { key: 'year', label: 'Year', sortable: true, class: '!text-left' },
    { key: 'player_name', label: 'Name', sortable: true, class: '!text-left' },
    {
      key: 'previous_team_name',
      label: 'Old Team',
      sortable: true,
      class: '!text-left',
    },
    {
      key: 'new_team_name',
      label: 'New Team',
      sortable: true,
      class: '!text-left',
    },
    {
      key: 'stat_percentile',
      label: 'Year',
      sortable: true,
      direction: 'desc',
    },
    {
      key: 'conf_adjust',
      label: 'Conference Adjust',
      sortable: true,
      direction: 'desc',
    },
    { key: 'twp', label: 'TWP', sortable: true, direction: 'desc' },
    {
      key: 'mlb_adjust',
      label: 'Pro Adjust',
      sortable: true,
      direction: 'desc',
    },
    { key: 'hs_adjust', label: 'HS Adjust', sortable: true, direction: 'desc' },
    {
      key: 'sixty_four_rating_portal_player',
      label: '64 Ranking',
      sortable: true,
      direction: 'desc',
    },
    { key: 'stats', label: 'Stats', sortable: false },
  ];
  if (!prpDictionary.value.length) return cols;

  cols.map((col) => {
    let localDictionaryRow = prpDictionary.value.find(
      (row) => row.characteristic == col.key
    );
    col.label = localDictionaryRow?.abbreviation || col.label;
  });
  return cols;
});
const {
  data: portalRankPlayers,
  refresh: refreshPortalRankPlayers,
  pending: portalRankPlayersLoading,
} = await useAsyncData(
  `team-${teamId}-portalRankPlayers`,
  async () => {
    try {
      const pagination = getQueryPagination(
        portalRankPlayersPage.value,
        portalRankPlayersPerPage.value
      );
      const query = supabase
        .from('portal_rank_player_v')
        .select(`*`, { count: 'exact' })
        .range(pagination.startIndex, pagination.endIndex)
        .limit(portalRankPlayersPerPage.value)
        .order(portalRankPlayersSort.value.column, {
          ascending: portalRankPlayersSort.value.direction == 'asc',
        });
      if (portalRankPlayersYear.value) {
        query.eq('year', portalRankPlayersYear.value);
      }
      query.eq('new_team_id', teamId).limit(1000);

      let { data, error, count } = await query;

      if (error) {
        console.error(error);
        throw error;
      }

      if (count) {
        portalRankPlayersTotal.value = count;
        portalRankPlayersPageCount.value = Math.ceil(
          count / portalRankPlayersPerPage.value
        );
      } else {
        portalRankPlayersTotal.value = 0;
        portalRankPlayersPageCount.value = 0;
      }

      const formattedData = prpParseData(data);

      return formattedData;
    } catch (error) {
      // Sentry.captureException(error);
      return [];
    }
  },
  {
    watch: [portalRankPlayersYear],
  }
);

const barYear = ref(2025);
const teamRankHittingSort = ref<{ column: string; direction: 'asc' | 'desc' }>({
  column: 'year',
  direction: 'desc',
});
const teamRankHittingPage = ref(1);
const teamRankHittingPerPage = ref(50);
const teamRankHittingTotal = ref(0);
const teamRankHittingPageCount = ref(0);
const TRH_TABLE_NAMES = ['hitting_team', 'team_rank', 'hitting', 'player_rank'];
const {
  parseData: trhParseData,
  localDictionary: teamRankHittingDictionary,
  formatData: formatTeamRankHittingData,
} = useDataDictionary(TRH_TABLE_NAMES);

const defaultHittingRankCols = (() => {
  let cols = transformColumns(
    teamRankHittingCols,
    teamRankHittingDictionary.value
  );
  // pull out the school and conference
  cols.splice(2, 2);
  return cols;
})();

const selectedHittingRankColumns = ref([...defaultHittingRankCols]);

const teamRankHittingColumns = computed((): Column[] => {
  return defaultHittingRankCols.filter((column) => {
    return selectedHittingRankColumns.value.find(
      (col) => col.key === column.key
    );
  });
});

const onSelectedHittingRankColumnsChange = ($event: Column[]) => {
  selectedHittingRankColumns.value = $event;
};

const {
  data: teamRankHitting,
  refresh: refreshTeamRankingsHitting,
  pending: teamRankHittingLoading,
} = await useAsyncData<Views<'team_rank_hitting'>[]>(
  `team-${teamId}-teamRankHitting`,
  async (): Promise<Views<'team_rank_hitting'>[]> => {
    try {
      if (!hasAccess.value) {
        const fakeRows = [];
        for (var i = 0; i < 4; i++) {
          const fakeRow = {} as Record<any, any>;
          teamRankHittingColumns.value?.map((col, index) => {
            fakeRow[col.key] =
              index == 0 ? 2024 : index == 1 ? 'Sign up for access ' : '0.000';
          });
          fakeRows.push(fakeRow);
        }
        return fakeRows as Views<'team_rank_hitting'>[];
      }
      const pagination = getQueryPagination(
        teamRankHittingPage.value,
        teamRankHittingPerPage.value
      );
      const query = supabase
        .from('team_rank_hitting')
        .select(`*`, { count: 'exact' })
        .range(pagination.startIndex, pagination.endIndex)
        .limit(teamRankHittingPerPage.value)
        .eq('team_id', team.value.id)
        .order(teamRankHittingSort.value.column, {
          ascending: teamRankHittingSort.value.direction == 'asc',
        })
        .limit(1000);
      // if (teamRankHittingYear.value) {
      //   query.eq('year', teamRankHittingYear.value);
      // }

      let { data, error, count } = await query;

      if (error) {
        console.error(error);
        throw error;
      } else if (data == null) {
        throw 'useAsyncData returned null';
      }

      if (count) {
        teamRankHittingTotal.value = count;
        teamRankHittingPageCount.value = Math.ceil(
          count / teamRankHittingPerPage.value
        );
      } else {
        teamRankHittingTotal.value = 0;
        teamRankHittingPageCount.value = 0;
      }

      const formattedData = trhParseData<Views<'team_rank_hitting'>>(data);

      return formattedData;
    } catch (error) {
      // Sentry.captureException(error);
      return [];
    }
  }
);
const barYearHitting = computed(() => {
  return teamRankHitting.value?.find((row) => row.year == barYear.value);
});
const hittingBarData = computed(
  (): {
    label: string;
    value: number;
    rawValue: number | string;
  }[] => {
    const hittingRankColsWithPercentiles = [];
    const percentileFields = [
      'percentile_rank_on_base_plus_slugging',
      'percentile_rank_weighted_on_base_average',
      'percentile_rank_isolated_power',
      'percentile_rank_weighted_runs_created',
      'percentile_rank_weighted_runs_above_average',
      'percentile_rank_runs_plate_appearance',
    ];
    return percentileFields.map((percentileField) => {
      const field = percentileField.replaceAll('percentile_rank_', '');
      const dictionaryRow = teamRankHittingDictionary.value.find(
        (row) => row.characteristic == field
      );
      return {
        label: dictionaryRow?.abbreviation,
        value: barYearHitting.value
          ? (parseFloat(barYearHitting.value[percentileField]) * 100).toFixed(2)
          : '',
        rawValue:
          teamRankHitting.value && dictionaryRow
            ? formatTeamRankHittingData(
                dictionaryRow,
                barYearHitting.value
                  ? barYearHitting.value[dictionaryRow.characteristic]
                  : ''
              )
            : '',
      };
    });
  }
);

const teamRankPitchingSort = ref<{ column: string; direction: 'asc' | 'desc' }>(
  {
    column: 'year',
    direction: 'desc',
  }
);
const teamRankPitchingPage = ref(1);
const teamRankPitchingPerPage = ref(50);
const teamRankPitchingTotal = ref(0);
const teamRankPitchingPageCount = ref(0);
const TRP_TABLE_NAMES = [
  'pitching_team',
  'team_rank',
  'pitching',
  'player_rank',
];
const {
  parseData: trpParseData,
  localDictionary: teamRankPitchingDictionary,
  formatData: formatTeamRankPitchingData,
} = useDataDictionary(TRP_TABLE_NAMES);
const defaultPitchingRankCols = (() => {
  let cols = transformColumns(
    teamRankPitchingCols,
    teamRankPitchingDictionary.value
  );
  // pull out the school and conference
  cols.splice(2, 2);
  return cols;
})();

const selectedPitchingRankColumns = ref([...defaultPitchingRankCols]);

const teamRankPitchingColumns = computed((): Column[] => {
  return defaultPitchingRankCols.filter((column) => {
    return selectedPitchingRankColumns.value.find(
      (col) => col.key === column.key
    );
  });
});

const onSelectedPitchingRankColumnsChange = ($event: Column[]) => {
  selectedPitchingRankColumns.value = $event;
};
const {
  data: teamRankPitching,
  refresh: refreshTeamRankingsPitching,
  pending: teamRankPitchingLoading,
} = await useAsyncData(`team-${teamId}-teamRankPitching`, async () => {
  try {
    if (!hasAccess.value) {
      const fakeRows = [];
      for (var i = 0; i < 4; i++) {
        const fakeRow = {} as Record<any, any>;
        teamRankPitchingColumns.value?.map((col, index) => {
          fakeRow[col.key] =
            index == 0 ? 2024 : index == 1 ? 'Sign up for access ' : '0.000';
        });
        fakeRows.push(fakeRow);
      }
      return fakeRows;
    }
    const pagination = getQueryPagination(
      teamRankPitchingPage.value,
      teamRankPitchingPerPage.value
    );
    const query = supabase
      .from('team_rank_pitching')
      .select(`*`, { count: 'exact' })
      .range(pagination.startIndex, pagination.endIndex)
      .limit(teamRankPitchingPerPage.value)
      .eq('team_id', team.value.id)
      .order(teamRankPitchingSort.value.column, {
        ascending: teamRankPitchingSort.value.direction == 'asc',
      })
      .limit(1000);
    // if (teamRankPitchingYear.value) {
    //   query.eq('year', teamRankPitchingYear.value);
    // }

    let { data, error, count } = await query;

    if (error) {
      console.error(error);
      throw error;
    }

    if (count) {
      teamRankPitchingTotal.value = count;
      teamRankPitchingPageCount.value = Math.ceil(
        count / teamRankPitchingPerPage.value
      );
    } else {
      teamRankPitchingTotal.value = 0;
      teamRankPitchingPageCount.value = 0;
    }

    const formattedData = trpParseData(data);

    return formattedData;
  } catch (error) {
    // Sentry.captureException(error);
    return [];
  }
});
const barYearPitching = computed(() => {
  return teamRankPitching.value?.find((row) => row.year == barYear.value);
});
const pitchingBarData = computed(
  (): {
    label: string;
    value: number;
    rawValue: number | string;
  }[] => {
    const pitchingRankColsWithPercentiles = [];
    const percentileFields = [
      'percentile_rank_on_base_plus_slugging_against',
      'percentile_rank_walk_percentage',
      'percentile_rank_strikeout_percentage',
      'percentile_rank_strikeout_to_walk_ratio',
      'percentile_rank_walks_plus_hits_per_inning_pitched',
      'percentile_rank_fielding_independent_pitching',
    ];
    return percentileFields.map((percentileField) => {
      const field = percentileField.replaceAll('percentile_rank_', '');
      const dictionaryRow = teamRankPitchingDictionary.value.find(
        (row) => row.characteristic == field
      );
      return {
        label: dictionaryRow?.abbreviation,
        value: barYearPitching.value
          ? (parseFloat(barYearPitching.value[percentileField]) * 100).toFixed(
              2
            )
          : '',
        rawValue:
          teamRankPitching.value && dictionaryRow
            ? formatTeamRankPitchingData(
                dictionaryRow,
                barYearPitching.value
                  ? barYearPitching.value[dictionaryRow.characteristic]
                  : ''
              )
            : '',
      };
    });
  }
);

const {
  data: depthChart,
  refresh: refreshDepthChart,
  pending: depthChartLoading,
} = await useAsyncData(
  `team-${teamId}-depthChart`,
  async () => {
    const query = supabase
      .from('depth_chart')
      .select(`*`)
      .order(depthChartSort.value.column, {
        ascending: depthChartSort.value.direction == 'asc',
      });
    if (depthChartYear.value) {
      query.eq('year', depthChartYear.value);
    }
    query.eq('team_id', teamId).limit(250);

    let { data: depthChart, error } = await query;

    if (error) {
      return error;
    }

    return depthChart;
  },
  {
    watch: [depthChartYear],
  }
);

const hittingStats = computed(() => {
  const sorted = team?.value?.hitting?.sort(
    (a, b) => (b.year || 0) - (a.year || 0)
  );
  const formatted = hittingParseData(sorted);
  return formatted;
});
const advancedHittingStats = computed(() => {
  if (!hasAccess.value) {
    const fakeRows = [];
    for (var i = 0; i < 4; i++) {
      const fakeRow = {} as Record<any, any>;
      advancedHittingCols.value?.map((col, index) => {
        fakeRow[col.key] =
          index == 0 ? 2024 : index == 1 ? 'Sign up for access ' : '0.000';
      });
      fakeRows.push(fakeRow);
    }
    return fakeRows;
  }
  const sorted = team?.value?.hitting?.sort(
    (a, b) => (b.year || 0) - (a.year || 0)
  );
  const formatted = hittingParseData(sorted);
  return formatted;
});
const pitchingStats = computed(() => {
  const sorted = team?.value?.pitching?.sort(
    (a, b) => (b.year || 0) - (a.year || 0)
  );
  const formatted = pitchingParseData(sorted);
  return formatted;
});
const advancedPitchingStats = computed(() => {
  if (!hasAccess.value) {
    const fakeRows = [];
    for (var i = 0; i < 4; i++) {
      const fakeRow = {} as Record<any, any>;
      advancedPitchingCols.value?.map((col, index) => {
        fakeRow[col.key] =
          index == 0 ? 2024 : index == 1 ? 'Sign up for access ' : '0.000';
      });
      fakeRows.push(fakeRow);
    }
    return fakeRows;
  }
  const sorted = team?.value?.pitching?.sort(
    (a, b) => (b.year || 0) - (a.year || 0)
  );
  const formatted = pitchingParseData(sorted);
  return formatted;
});
const fieldingStats = computed(() => {
  const sorted = team?.value?.fielding?.sort(
    (a, b) => (b.year || 0) - (a.year || 0)
  );
  const formatted = fieldingParseData(sorted);
  return formatted;
});

const tabs = [
  {
    key: 'depth-chart',
    label: 'Depth Chart',
    description: '',
  },
  {
    key: 'recruiting',
    label: 'Recruiting',
    description: '',
  },
  {
    key: 'coaching-staff',
    label: 'Coaching Staff',
    description: '',
  },
  {
    key: 'draft-history',
    label: 'Draft History',
    description: '',
  },
  {
    key: 'rankings',
    label: 'Rankings',
    description: '',
  },
  {
    key: 'stats',
    label: 'Stats',
    description: '',
  },
  {
    key: 'advanced-stats',
    label: 'Advanced Stats',
    description: '',
  },
  {
    key: 'development',
    label: 'Development',
    description: '',
  },
];
const currentTabKey = ref('depth-chart');
const currentTab = computed(() =>
  tabs.find((tab) => tab.key == currentTabKey.value)
);
const tabRef = ref<Element | null>(null);
const placeholderRef = ref<Element | null>(null);
const { headerVisible } = useHeadroom();
const { bounds, originalDistance, originalHeight, isSticky } = useSticky(
  tabRef,
  placeholderRef
);
</script>

<style>
.ad-card-wrapper > div {
  @apply p-0;
}
</style>
